import 'views/components/common/tag-selector/index.css'
import './index.css'
import 'components/ui/dropdowns/react-select-v5-theme.css'

import cn from 'clsx'
import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import Select, { components as selectComponents } from 'react-select'
import type { MenuProps, MultiValueProps } from 'react-select'

import type { NativeTagWithCategory } from 'types/track'
import type { DropdownOption } from 'types/ui'

import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getFlattenedNativeTags } from '@disco/state-management/selectors/tags'
import NativeTagsPopover from 'components/common/native-tags-popover'
import Icon from 'components/ui/icon'
import Tag from 'components/ui/tag'

const mapStateToProps = (state): any => {
  return {
    tags: getFlattenedNativeTags(state),
  }
}

type Props = {
  onChange: (tags: DropdownOption[]) => void
  selectedTags: number[]
  tags: NativeTagWithCategory[]
  placeholder: string
  disabled?: boolean
  className?: string
  disabledTags?: string[]
  scrollContainer?: HTMLDivElement | null | undefined
}

type TagOption = {
  value: number
  tag: NativeTagWithCategory
  label: string
}

/**
 * Native tags selector component with search and multi-selection functionality
 * Integrates with react-select and displays tags with color indicators
 * Supports scrollable containers and custom positioning
 * @param onChange - Callback when tag selection changes
 * @param selectedTags - Array of selected tag IDs
 * @param tags - Available tags with categories
 * @param placeholder - Placeholder text for the selector
 * @param disabled - Whether the selector is disabled
 * @param className - Additional CSS classes
 * @param disabledTags - Array of disabled tag names
 * @param scrollContainer - Container for scroll-aware positioning
 *
 */
const NativeTagsSelector = (props: Props) => {
  const {
    selectedTags,
    disabled,
    className,
    placeholder,
    onChange,
    tags,
    scrollContainer,
  } = props
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const tagOptions: TagOption[] = useMemo(
    () =>
      tags.map(t => ({
        // `value` is for displaying `selectedTags`
        value: t.id,
        // `tag` is for rendering the value chip
        tag: t,
        // `label` is for search
        label: t.name + ' - ' + t.category.name,
      })),
    [tags],
  )

  // v5 selects by option object, so resolve the stored ids to their options.
  const selectedOptions = useMemo(
    () => tagOptions.filter(o => selectedTags.includes(o.value)),
    [tagOptions, selectedTags],
  )

  const DropdownIndicator = useCallback(
    (indicatorProps: any) => (
      <selectComponents.DropdownIndicator {...indicatorProps}>
        <Icon type="arrow-down" />
      </selectComponents.DropdownIndicator>
    ),
    [],
  )

  const MultiValue = useCallback(
    (multiValueProps: MultiValueProps<TagOption, true>) => {
      const { tag } = multiValueProps.data
      return (
        <span className="Select-tag">
          <Tag
            tag={tag.name}
            tagColor={colorIndicatorsEnabled ? tag.color : undefined}
            onRemoveClick={
              disabled
                ? undefined
                : () =>
                    multiValueProps.removeProps.onClick?.({
                      preventDefault() {},
                      stopPropagation() {},
                    } as any)
            }
          />
        </span>
      )
    },
    [colorIndicatorsEnabled, disabled],
  )

  // NativeTagsPopover is the whole menu; it drives selection through the same
  // inReactSelect contract the v1 menuRenderer provided, rebuilt from v5's props.
  const Menu = useCallback(
    (menuProps: MenuProps<TagOption, true>) => {
      const valueArray = (menuProps.getValue() ?? []) as TagOption[]
      const inReactSelect = {
        inputValue: menuProps.selectProps.inputValue,
        valueArray,
        selectValue: ({ value }: { value: number }) => {
          const option = tagOptions.find(o => o.value === value)
          if (option) menuProps.selectOption(option)
        },
        removeValue: ({ value }: { value: number }) => {
          onChange(
            valueArray.filter(o => o.value !== value) as DropdownOption[],
          )
        },
      }
      return (
        <div
          onMouseDown={event => event.preventDefault()}
          style={
            scrollContainer
              ? { width: '100%' }
              : { position: 'absolute', top: 0, width: '100%' }
          }
        >
          <NativeTagsPopover inReactSelect={inReactSelect} />
        </div>
      )
    },
    [tagOptions, onChange, scrollContainer],
  )

  return (
    <div className="NativeTagsSelector">
      <Select<TagOption, true>
        classNamePrefix="Select"
        className={cn('Select', className)}
        isMulti
        isClearable={false}
        closeMenuOnSelect
        // NativeTagsPopover does its own filtering from inputValue.
        filterOption={null}
        options={tagOptions}
        value={selectedOptions}
        placeholder={placeholder}
        onChange={newValue => onChange(newValue as unknown as DropdownOption[])}
        isDisabled={disabled}
        menuPosition={scrollContainer ? 'fixed' : 'absolute'}
        menuPortalTarget={
          scrollContainer && typeof document !== 'undefined'
            ? document.body
            : undefined
        }
        components={{
          Menu,
          MultiValue,
          DropdownIndicator,
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  )
}

export default connect(mapStateToProps)(NativeTagsSelector) as any
